// Pathfinder placement gating. Pure. No React, no Three.js.
//
// ── Codex (Pathfinder) ──
//   "Tap → If able, play the topmost site of your atlas to an adjacent
//    location and move there."
//
// "Adjacent" = cardinal-neighbor (4-direction). The site can only be
// dropped on a cell cardinal-adjacent to the avatar; the avatar then
// auto-moves to that cell. The "If able" gate means the activation
// fizzles when the atlas is empty OR its top card isn't a Site.
//
// Exception. First turn: the avatar starts in the void. Per the codex
// "Avatar in Void" rule the FIRST site goes UNDER the avatar (its own
// cell), ignoring normal adjacency. Pathfinder draws no sites during
// setup, so its tap ability is the only way to resolve that mandatory
// placement. The placement gate has to allow the under-avatar cell.

import { adjacentVoidCells } from './geomancer';

/**
 * Predicate for the Pathfinder placement constraint.
 *
 * @param targetCell { col, row } the drop cell
 * @param avatarCell { col, row } the Pathfinder's current cell
 * @returns true when targetCell is cardinal-adjacent to avatarCell
 */
export function isCardinalAdjacent(targetCell, avatarCell) {
  if (!targetCell || !avatarCell) return false;
  const dx = Math.abs(targetCell.col - avatarCell.col);
  const dy = Math.abs(targetCell.row - avatarCell.row);
  return (dx + dy) === 1;
}

/**
 * Check whether Pathfinder can fire. Atlas non-empty AND topmost card
 * is a Site. Returns the top card's catalog entry on success, null on
 * fizzle.
 *
 * @param atlasPile pile { cards: [...] }
 * @param getFullCard (cardId) => catalog entry
 */
export function topAtlasSite(atlasPile, getFullCard) {
  if (!atlasPile || !atlasPile.cards || atlasPile.cards.length === 0) return null;
  const top = atlasPile.cards[atlasPile.cards.length - 1];
  const full = getFullCard(top.cardId);
  if (full?.type !== 'Site') return null;
  return { instance: top, fullCard: full };
}

/**
 * Legal target cells for a Pathfinder tap, as `'col,row'` keys.
 *
 * Three cases, mirroring the official Pathfinder FAQ:
 *
 * - In the void on the FIRST turn → only the under-avatar cell. The
 *   base "Avatar in Void" rule forces the first site of the game under
 *   the avatar, ignoring normal adjacency.
 * - In the void on a LATER turn (all sites destroyed, e.g. Roots of
 *   Yggdrasil) → under-avatar OR adjacent. You are NOT forced under;
 *   Pathfinder's ability is an alternate play, so adjacent is allowed.
 * - On a site → cardinal-adjacent void cells only, then move there.
 *
 * @param avatarCell  { col, row } | null  the Pathfinder's current cell
 * @param inVoid      boolean              avatar is in the void
 * @param isFirstTurn boolean              controller's first turn of the game
 * @param sitedCells  Set<'col,row'>       cells already holding a site
 * @param totalCols   number               grid column count
 * @param totalRows   number               grid row count
 */
export function pathfinderPlacementCells(avatarCell, inVoid, isFirstTurn, sitedCells, totalCols, totalRows) {
  if (!avatarCell) return new Set();
  const underKey = `${avatarCell.col},${avatarCell.row}`;
  if (inVoid && isFirstTurn) return new Set([underKey]);
  const cells = adjacentVoidCells(avatarCell, sitedCells, totalCols, totalRows);
  if (inVoid) cells.add(underKey);
  return cells;
}
